import { useContext, useEffect, useState } from "react";
import { useRouter } from "next/router";

import { AppContext } from "@vahak/core/dist/app-context";
import { toast } from "@vahak/core/dist/components/toast/toast";
import { LOAD_STATUS } from "@vahak/core/dist/constants/loadStatus";
import { useCsDashboard } from "@vahak/core/dist/hooks/useCsDashboard";
import { useToggleModalWithLocalState } from "@vahak/core-ui/dist/hooks/useToggleModalWithLocalState";

import { MARKETPLACE_ACTIONS, MarketplaceContext } from "../../../../MarketplaceContextProvider";
import { LoadMarketCard } from "../../../../services/load/useGetLoadMarketV2/type";
import { useGetLoadMarketV2New } from "../../../../services/load/useGetLoadMarketV2/useGetLoadMarketV2";
import { useCompactLoadBidding } from "../../../homepage-v2/compact-load-bidding/CompactLoadBiddingProvider";

/** Same module-scope latch as MarketPlacePage — open bid once after shared-load fetch. */
let shouldOpenBidFormOnPageLoad = false;

/**
 * Shared `?loadbid=` deep-link flow for marketplace v3 (mirrors MarketPlacePage).
 */
export function useMarketplaceV3SharedLoadBid() {
    const router = useRouter();
    const { companyId, customerCompanyId, setShouldPreventLayoutSwitch } = useContext(AppContext);
    const { dispatchMarketplace } = useContext(MarketplaceContext);
    const { isCsDashboardUser } = useCsDashboard();
    const { openCompactBidding } = useCompactLoadBidding();

    const requiredCompanyId = Number(customerCompanyId || companyId) || undefined;
    const isLoadBid = Boolean(router.query["loadbid"]);
    const loadLorryId = router.query["loadbid"] || router.query["lorrybid"];

    const [sharedLoad, setSharedLoad] = useState<LoadMarketCard>();
    const loadNotAvailablePopup = useToggleModalWithLocalState(false);
    const [getSimilarLoad, setGetSimilarLoad] = useState<Function>();

    useGetLoadMarketV2New(
        {
            preloads: ["company_detail", "bid_infos"],
            isAdmin: isCsDashboardUser,
            loadId: loadLorryId ? [Number(loadLorryId)] : undefined,
            c_i: requiredCompanyId
        },
        {
            onSuccess(data) {
                shouldOpenBidFormOnPageLoad = true;
                const resultLoad = data.load_cards?.[0];
                if (resultLoad?.load_detail?.status === LOAD_STATUS.ACTIVE) {
                    !!resultLoad && setSharedLoad(resultLoad);
                } else if (resultLoad?.load_detail?.id) {
                    setGetSimilarLoad(() => {
                        dispatchMarketplace({
                            type: MARKETPLACE_ACTIONS.APPLY_FILTER,
                            payload: {
                                srcStateId: resultLoad.load_detail.source_state_id
                            }
                        });
                        router.push({ pathname: router.asPath?.split("?")?.[0], query: {} }, undefined, {
                            shallow: true
                        });
                    });
                    loadNotAvailablePopup.openModal();
                }
            },
            onError() {},
            retry: 0,
            refetchOnWindowFocus: false,
            enabled: isLoadBid && Boolean(loadLorryId)
        }
    );

    useEffect(() => {
        if (!sharedLoad || !shouldOpenBidFormOnPageLoad) {
            return;
        }

        if (sharedLoad.load_detail.company_id != requiredCompanyId) {
            setShouldPreventLayoutSwitch?.(true);
            openCompactBidding({
                loadData: sharedLoad,
                trackRank: 0
            });
        } else {
            toast.warning("You can't Bid on this load");
        }

        shouldOpenBidFormOnPageLoad = false;

        const newQuery = { ...router.query };
        delete newQuery["loadbid"];
        delete newQuery["lorrybid"];
        router.replace(
            {
                pathname: router.pathname,
                query: newQuery
            },
            undefined,
            { shallow: true }
        );
        // Mirror MarketPlacePage: open once when shared load arrives.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sharedLoad, requiredCompanyId, loadLorryId]);

    return {
        loadNotAvailablePopup,
        getSimilarLoad
    };
}
